import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformSDKTimestampToRESTTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveWixPaymentsRefundsV1RefundServiceUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'manage._base_domain_': [
      {
        srcPath: '/_api/payments/refunds/v1',
        destPath: '/v1',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/payments/refunds/v1/refunds',
        destPath: '/v1/refunds',
      },
    ],
    '*.dev.wix-code.com': [
      {
        srcPath: '/payments/refunds/v1/refunds',
        destPath: '/v1/refunds',
      },
    ],
    _: [
      {
        srcPath: '/payments/refunds/v1/refunds',
        destPath: '/v1/refunds',
      },
    ],
    'manage.base44.com': [
      {
        srcPath: '/_api/refunds',
        destPath: '',
      },
    ],
    'payments.base44.com': [
      {
        srcPath: '/_api/refunds',
        destPath: '',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_payments_refunds';

/**
 * Creates a refund.
 *
 * The refunding process starts immediately after a refund entity is created.
 *
 * If you specify an `amount`, you must also specify a `currency_code`. This currency should be the same as the currency of the charge.
 * If `amount` and `currency_code` are not specified, the refund is created for the full charge amount. If the charge was already partially refunded, this refund will fail.
 */
export function createRefund(payload: object): RequestOptionsFactory<any> {
  function __createRefund({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [{ path: 'refund.createdDate' }, { path: 'refund.updatedDate' }],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.payments.refunds.v1.refund',
      method: 'POST' as any,
      methodFqn: 'wix.payments.refunds.v1.RefundService.CreateRefund',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixPaymentsRefundsV1RefundServiceUrl({
        protoPath: '/v1/refunds',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'refund.createdDate' },
              { path: 'refund.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __createRefund;
}

/** Retrieves a refund. */
export function getRefund(payload: object): RequestOptionsFactory<any> {
  function __getRefund({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.payments.refunds.v1.refund',
      method: 'GET' as any,
      methodFqn: 'wix.payments.refunds.v1.RefundService.GetRefund',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixPaymentsRefundsV1RefundServiceUrl({
        protoPath: '/v1/refunds/{refundId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'refund.createdDate' },
              { path: 'refund.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __getRefund;
}

/**
 * Creates a query to retrieve a list of refunds.
 *
 *
 * The `queryRefunds()` method builds a query to retrieve a list of refunds and returns a [RefundsQueryBuilder](https://dev.wix.com/docs/sdk/backend-modules/payments/refunds/refunds-query-builder/ascending) object.
 *
 * The returned object contains the query definition which is typically used to run the query using the [`find()`](https://dev.wix.com/docs/sdk/backend-modules/payments/refunds/refunds-query-builder/find) method.
 *
 * You can refine the query by chaining `RefundsQueryBuilder` methods onto the query. `RefundsQueryBuilder` methods enable you to sort, filter, and control the results that `queryRefunds.find()` returns.
 *
 * The query runs with the following `RefundsQueryBuilder` defaults that you can override:
 *
 * - [`skipTo(0)`](https://dev.wix.com/docs/sdk/backend-modules/payments/refunds/refunds-query-builder/skip-to)
 * - [`limit(50)`](https://dev.wix.com/docs/sdk/backend-modules/payments/refunds/refunds-query-builder/limit)
 * - [`descending("_createdDate")`](https://dev.wix.com/docs/sdk/backend-modules/payments/refunds/refunds-query-builder/descending)
 *
 * The methods that are chained to `queryRefunds()` are applied in the order they are called.
 *
 * The table below shows which `RefundsQueryBuilder` methods are supported for `queryRefunds()`. You can only use one filter method for each property. Only the first filter will work if a property is used in more than one filter.
 */
export function queryRefunds(payload: object): RequestOptionsFactory<any> {
  function __queryRefunds({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.payments.refunds.v1.refund',
      method: 'POST' as any,
      methodFqn: 'wix.payments.refunds.v1.RefundService.QueryRefunds',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixPaymentsRefundsV1RefundServiceUrl({
        protoPath: '/v1/refunds/query',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'refunds.createdDate' },
              { path: 'refunds.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __queryRefunds;
}
